import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { WizardType } from '../store/wizardStore';
import { useWizardStore } from '../store/wizardStore';
import { fetchCatalogByCategory, type SywComponent } from '../hooks/useSywData';
import { StepperNav } from '../components/StepperNav';
import { swatchHex } from '../lib/catalogModel';

// Step 3 — bulk colour. One selector that swaps every selected espelho/módulo
// to the colour variant of the same product (Shopify variant exchange).
// SYW-fallback rows carry no colour data → informational note + skip.

export function ColorPage() {
  const { type } = useParams<{ type: WizardType }>();
  const navigate = useNavigate();
  const { divisions, selections, globalColor, setGlobalColor, replaceSelections } =
    useWizardStore();

  const [rows, setRows] = useState<SywComponent[]>([]);
  const [loading, setLoading] = useState(true);

  const selectedSkus = useMemo(
    () => [...new Set(Object.values(selections).flat())],
    [selections],
  );

  // Load every opted-in division's catalog — variant siblings (other colours
  // of the same product) only exist on these Shopify-sourced rows.
  useEffect(() => {
    let cancelled = false;
    const categories = [
      ...new Set(
        divisions
          .filter((d) => d.optedIn && d.categoryId)
          .map((d) => d.categoryId as string),
      ),
    ];
    if (categories.length === 0) {
      setLoading(false);
      return;
    }
    Promise.allSettled(categories.map((c) => fetchCatalogByCategory(c)))
      .then((results) => {
        if (cancelled) return;
        setRows(
          results.flatMap((r) => (r.status === 'fulfilled' ? r.value : [])),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [divisions]);

  const bySku = useMemo(() => {
    const m = new Map<string, SywComponent>();
    for (const r of rows) if (!m.has(r.sku)) m.set(r.sku, r);
    return m;
  }, [rows]);

  const byProduct = useMemo(() => {
    const m = new Map<string, SywComponent[]>();
    for (const r of rows) {
      if (!r.productId) continue;
      const list = m.get(r.productId) ?? [];
      list.push(r);
      m.set(r.productId, list);
    }
    return m;
  }, [rows]);

  // Colours offered = colours available on products the customer selected,
  // with a count of how many selected items each colour can apply to.
  const colorOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const sku of selectedSkus) {
      const row = bySku.get(sku);
      if (!row?.productId) continue;
      const siblings = byProduct.get(row.productId) ?? [];
      for (const color of new Set(
        siblings.filter((s) => s.colorName && s.active).map((s) => s.colorName as string),
      )) {
        counts.set(color, (counts.get(color) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([color, count]) => ({ color, count }));
  }, [selectedSkus, bySku, byProduct]);

  function applyColor(color: string) {
    const mapping: Record<string, string> = {};
    for (const sku of selectedSkus) {
      const row = bySku.get(sku);
      if (!row?.productId || row.colorName === color) continue;
      const sibling = (byProduct.get(row.productId) ?? []).find(
        (s) => s.colorName === color && s.active,
      );
      if (sibling && sibling.sku !== sku) mapping[sku] = sibling.sku;
    }
    replaceSelections(mapping);
    setGlobalColor(color);
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <StepperNav current={3} />

      <div className="mb-6 mt-4">
        <button
          type="button"
          onClick={() => navigate(`/wizard/${type}`)}
          className="mb-4 text-sm text-smartify-muted hover:text-smartify-ink"
        >
          ← Voltar às divisões
        </button>
        <h1 className="text-xl font-bold text-smartify-ink">Escolhe a cor</h1>
        <p className="mt-1 text-sm text-smartify-muted">
          Aplica uma cor a todos os espelhos e módulos de uma vez. Podes mudar depois.
        </p>
      </div>

      {loading ? (
        <div className="rounded-smartify border border-smartify-line bg-white p-8 text-center text-sm text-smartify-muted">
          A carregar cores disponíveis…
        </div>
      ) : colorOptions.length === 0 ? (
        <div className="rounded-smartify border border-smartify-line bg-white p-6 text-center text-sm text-smartify-muted">
          Os produtos selecionados não têm variantes de cor disponíveis de momento.
          <br />
          Podes continuar — a cor é confirmada no orçamento.
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {colorOptions.map(({ color, count }) => {
            const selected = globalColor === color;
            return (
              <li key={color}>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => applyColor(color)}
                  className={[
                    'flex w-full items-center gap-3 rounded-smartify border p-3 text-left transition-colors',
                    selected
                      ? 'border-smartify-brand bg-blue-50'
                      : 'border-smartify-line bg-white hover:border-smartify-accent',
                  ].join(' ')}
                >
                  <span
                    className="h-8 w-8 shrink-0 rounded-full border border-smartify-line"
                    style={{ backgroundColor: swatchHex(color) }}
                    aria-hidden
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-smartify-ink">
                      {color}
                    </span>
                    <span className="block text-xs text-smartify-muted">
                      {count} {count === 1 ? 'artigo' : 'artigos'}
                    </span>
                  </span>
                  {selected && (
                    <span className="ml-auto text-smartify-brand" aria-hidden>
                      ✓
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-8 flex items-center justify-end gap-3">
        {globalColor && (
          <button
            type="button"
            onClick={() => setGlobalColor(null)}
            className="text-sm text-smartify-muted underline hover:text-smartify-ink"
          >
            Limpar cor
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate('/cart')}
          className="rounded-smartify bg-smartify-brand px-8 py-3 font-semibold text-white transition-opacity hover:opacity-90"
        >
          Continuar →
        </button>
      </div>
    </div>
  );
}
